import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem 
} from '@mui/material';
import NotesIcon from '@mui/icons-material/Notes';
import { useTranslation } from 'react-i18next';

/**
 * DocumentNotesSection Component
 * Template selector and custom notes input
 * Reusable across Delivery Notes, Sale Orders, and Invoices
 */
const DocumentNotesSection = ({
  customText = '',
  onCustomTextChange,
  noteTemplates = [],
  selectedTemplate,
  onTemplateSelect,
  maxLength = 1000,
  stripHtml,
}) => {
  const { t } = useTranslation();

  const strippedLength = stripHtml ? stripHtml(customText).length : customText.length;

  return (
    <Box sx={{
      p: 3,
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      borderLeft: '4px solid #667eea',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <Typography 
        variant="subtitle2" 
        fontWeight="700" 
        color="#2c3e50" 
        sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}
      >
        <NotesIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
        {t("notes")}
      </Typography>
      
      {/* Template Selector */}
      {noteTemplates.length > 0 && onTemplateSelect && (
        <FormControl fullWidth sx={{ mb: 2, bgcolor: 'white' }}>
          <InputLabel>{t("select_template")}</InputLabel>
          <Select
            value={selectedTemplate || ''}
            onChange={(e) => onTemplateSelect(e.target.value)}
            label={t("select_template")}
          >
            <MenuItem value="">
              <em>{t("none")}</em>
            </MenuItem>
            {noteTemplates.map((template) => (
              <MenuItem key={template.Id} value={template.Id}>
                {template.TemplateName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {/* Notes Text Area */}
      <TextField
        multiline
        rows={4}
        value={customText}
        onChange={(e) => {
          const newText = e.target.value;
          const newLength = stripHtml ? stripHtml(newText).length : newText.length;
          if (newLength <= maxLength) {
            onCustomTextChange(newText);
          }
        }}
        placeholder={t("add_custom_notes") || "Add custom notes..."}
        fullWidth
        sx={{
          bgcolor: 'white',
          borderRadius: '4px'
        }}
      />
      <Typography
        variant="caption"
        color={strippedLength > maxLength ? 'error' : 'text.secondary'}
        sx={{ mt: 1, display: 'block' }}
      >
        {strippedLength} / {maxLength}
      </Typography>
    </Box>
  );
};

export default DocumentNotesSection;
